'use client'

import {
  useEffect,
  useRef,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import {
  Box,
  CircularProgress,
  Skeleton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import type { DefaultSearchCardsData } from '@query/feature-query/default-search-cards'
import { SCROLL_ID } from '@utils/scroll'
import { getStyles, STYLE_SIZE_CONSTANTS } from '@utils/styles'
import {
  buildSiteSearchHref,
  countGroupedResults,
  countResourceResults,
  parseSiteSearchCategory,
  SEARCH_CATEGORY_PARAM,
  SEARCH_CARDS_PER_CATEGORY,
  SEARCH_QUERY_PARAM,
  SEARCH_SECTION_IDS,
  SEARCH_SECTION_META,
  SEARCH_TABS_ELEMENT_ID,
  SITE_SEARCH_CATEGORY,
  SITE_SEARCH_TAB,
  SITE_SEARCH_TAB_LABELS,
  SITE_SEARCH_TABS,
  useSiteWideSearch,
  type SiteSearchContentCategory,
  type SiteSearchGroupedItems,
  type SiteSearchTab,
} from 'src/client-query/search'
import { DefaultSearchCards } from './DefaultSearchCards'
import { SearchEmptyBanner } from './SearchEmptyBanner'
import defaultStyles from './styles'

const SEARCH_DEBOUNCE_MS = 600
const SEARCH_SCROLL_GAP_PX = 16
/** Matches ResourceListSection URL param on /blog and /case-studies */
const RESOURCE_LIST_SEARCH_PARAM = 'search'
const DEFAULT_NO_RESULTS_MESSAGE =
  'Your search did not match any services. You may wish to browse the available services below or contact us for assistance.'

type SearchPageProps = {
  defaultSearchCards?: DefaultSearchCardsData
  searchNoResultsMessage?: string
}

function buildResourceSearchHref(href: string, query: string) {
  const trimmed = query.trim()
  if (!trimmed) return href

  const params = new URLSearchParams()
  params.set(RESOURCE_LIST_SEARCH_PARAM, trimmed)
  return `${href}?${params.toString()}`
}

function getSearchScrollOffset() {
  const header = document.getElementById(SCROLL_ID.Header)
  const tabs = document.getElementById(SEARCH_TABS_ELEMENT_ID)

  return (
    (header?.getBoundingClientRect().height ??
      STYLE_SIZE_CONSTANTS.headerHeight) +
    (tabs?.getBoundingClientRect().height ?? 0) +
    SEARCH_SCROLL_GAP_PX
  )
}

function scrollToSearchSection(id: string) {
  const target = document.getElementById(id)
  if (!target) return

  const top =
    target.getBoundingClientRect().top +
    window.scrollY -
    getSearchScrollOffset()

  window.scrollTo({
    top: Math.max(0, top),
    left: 0,
    behavior: 'smooth',
  })
}

function useStickySearchTabsTop() {
  const [tabsStickyTop, setTabsStickyTop] = useState(
    STYLE_SIZE_CONSTANTS.headerHeight
  )

  useEffect(() => {
    const header = document.getElementById(SCROLL_ID.Header)
    if (!header) return undefined

    const updateTop = () => {
      setTabsStickyTop(Math.round(header.getBoundingClientRect().height))
    }

    updateTop()
    const observer = new ResizeObserver(updateTop)
    observer.observe(header)
    return () => observer.disconnect()
  }, [])

  return tabsStickyTop
}

function useSearchScrollNavigation({
  hasQuery,
  isLoading,
  resultCount,
  groups,
  categoryFromUrl,
  qFromUrl,
}: {
  hasQuery: boolean
  isLoading: boolean
  resultCount: number
  groups?: SiteSearchGroupedItems
  categoryFromUrl: SiteSearchTab
  qFromUrl: string
}) {
  const router = useRouter()
  const [activeCategory, setActiveCategory] =
    useState<SiteSearchTab>(categoryFromUrl)
  const didInitialScroll = useRef(false)
  const scrollingFromTab = useRef(false)
  const scrollSpyTimeout = useRef<ReturnType<typeof setTimeout>>()

  useEffect(() => {
    setActiveCategory(categoryFromUrl)
  }, [categoryFromUrl])

  useEffect(() => {
    didInitialScroll.current = false
  }, [qFromUrl])

  const pauseScrollSpy = (durationMs = 1200) => {
    scrollingFromTab.current = true
    if (scrollSpyTimeout.current) {
      window.clearTimeout(scrollSpyTimeout.current)
    }
    scrollSpyTimeout.current = window.setTimeout(() => {
      scrollingFromTab.current = false
    }, durationMs)
  }

  useEffect(() => {
    return () => {
      if (scrollSpyTimeout.current) {
        window.clearTimeout(scrollSpyTimeout.current)
      }
    }
  }, [])

  useEffect(() => {
    if (
      isLoading ||
      !hasQuery ||
      resultCount === 0 ||
      didInitialScroll.current
    ) {
      return undefined
    }

    didInitialScroll.current = true
    if (categoryFromUrl === SITE_SEARCH_TAB.All) {
      return undefined
    }

    pauseScrollSpy()
    const frame = window.requestAnimationFrame(() => {
      scrollToSearchSection(SEARCH_SECTION_IDS[categoryFromUrl])
    })
    return () => window.cancelAnimationFrame(frame)
  }, [isLoading, hasQuery, resultCount, categoryFromUrl])

  useEffect(() => {
    if (!hasQuery || isLoading || resultCount === 0) {
      return undefined
    }

    const visibleTabs: SiteSearchTab[] = []
    if (groups?.[SITE_SEARCH_CATEGORY.Services]?.length) {
      visibleTabs.push(SITE_SEARCH_TAB.Services)
    }
    if (countResourceResults(groups) > 0) {
      visibleTabs.push(SITE_SEARCH_TAB.Resources)
    }
    if (groups?.[SITE_SEARCH_CATEGORY.Industries]?.length) {
      visibleTabs.push(SITE_SEARCH_TAB.Industries)
    }
    if (groups?.[SITE_SEARCH_CATEGORY.Hire]?.length) {
      visibleTabs.push(SITE_SEARCH_TAB.Hire)
    }

    let frame = 0
    const updateActiveTabFromScroll = () => {
      if (scrollingFromTab.current) return

      const probeY = getSearchScrollOffset() + 1
      let next: SiteSearchTab = SITE_SEARCH_TAB.All

      visibleTabs.forEach((tab) => {
        const section = document.getElementById(SEARCH_SECTION_IDS[tab])
        if (!section) return
        if (section.getBoundingClientRect().top <= probeY) {
          next = tab
        }
      })

      setActiveCategory((current) => (current === next ? current : next))
    }

    const onScroll = () => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        updateActiveTabFromScroll()
      })
    }

    window.addEventListener('scroll', onScroll, { passive: true })
    updateActiveTabFromScroll()

    return () => {
      window.removeEventListener('scroll', onScroll)
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [hasQuery, isLoading, resultCount, groups])

  const handleCategoryChange = (_: SyntheticEvent, nextIndex: number) => {
    if (nextIndex < 0 || nextIndex >= SITE_SEARCH_TABS.length) return

    const nextCategory = SITE_SEARCH_TABS[nextIndex]

    pauseScrollSpy()
    setActiveCategory(nextCategory)
    router.replace(
      buildSiteSearchHref({ q: qFromUrl, category: nextCategory }),
      { scroll: false }
    )
    // Wait a frame so the sticky offset is measured after tab state updates.
    window.requestAnimationFrame(() => {
      scrollToSearchSection(SEARCH_SECTION_IDS[nextCategory])
    })
  }

  return { activeCategory, handleCategoryChange }
}

function getSearchSectionCardsData(
  category: SiteSearchContentCategory,
  groups: SiteSearchGroupedItems,
  searchQuery: string
) {
  const meta = SEARCH_SECTION_META[category]

  return {
    sectionHeaderData: {
      heading: meta.heading,
      ...(meta.viewAllLabel && meta.href
        ? {
            actionButtonData: {
              children: meta.viewAllLabel,
              link: buildResourceSearchHref(meta.href, searchQuery),
            },
          }
        : {}),
    },
    cardsData: groups[category].map((item) => item.card),
  }
}

function getServiceCtaLabel(category: SiteSearchContentCategory) {
  if (category === SITE_SEARCH_CATEGORY.Services) return 'View Service'
  if (
    category === SITE_SEARCH_CATEGORY.Industries ||
    category === SITE_SEARCH_CATEGORY.Hire
  ) {
    return 'View Details'
  }
  return undefined
}

function SearchCategoryCards({
  category,
  groups,
  searchQuery,
}: {
  category: SiteSearchContentCategory
  groups: SiteSearchGroupedItems
  searchQuery: string
}) {
  const meta = SEARCH_SECTION_META[category]
  const variantByCategory = {
    [SITE_SEARCH_CATEGORY.Services]: 'service',
    [SITE_SEARCH_CATEGORY.Industries]: 'service',
    [SITE_SEARCH_CATEGORY.Hire]: 'service',
    [SITE_SEARCH_CATEGORY.Blogs]: 'blog',
    [SITE_SEARCH_CATEGORY.CaseStudies]: 'caseStudy',
  } as const

  const isPageCategory =
    category === SITE_SEARCH_CATEGORY.Services ||
    category === SITE_SEARCH_CATEGORY.Industries ||
    category === SITE_SEARCH_CATEGORY.Hire

  return (
    <DefaultSearchCards
      resourceType={meta.resourceType}
      variant={variantByCategory[category]}
      truncateDescription
      descriptionLineClamp={isPageCategory ? 4 : 2}
      serviceCtaLabel={getServiceCtaLabel(category)}
      data={getSearchSectionCardsData(category, groups, searchQuery)}
    />
  )
}

function SearchResultSections({
  groups,
  searchQuery,
}: {
  groups: SiteSearchGroupedItems
  searchQuery: string
}) {
  const styles = getStyles(defaultStyles)
  const hasServices = groups[SITE_SEARCH_CATEGORY.Services].length > 0
  const hasIndustries = groups[SITE_SEARCH_CATEGORY.Industries].length > 0
  const hasHire = groups[SITE_SEARCH_CATEGORY.Hire].length > 0
  const hasBlogs = groups[SITE_SEARCH_CATEGORY.Blogs].length > 0
  const hasCaseStudies = groups[SITE_SEARCH_CATEGORY.CaseStudies].length > 0
  const hasResourceColumns = hasBlogs || hasCaseStudies

  // Tab order: Services → Resources → Industries → Hire
  const sections: Array<{ key: string; node: ReactNode }> = []

  if (hasServices) {
    sections.push({
      key: SITE_SEARCH_CATEGORY.Services,
      node: (
        <SearchCategoryCards
          category={SITE_SEARCH_CATEGORY.Services}
          groups={groups}
          searchQuery={searchQuery}
        />
      ),
    })
  }

  if (hasResourceColumns) {
    sections.push({
      key: SITE_SEARCH_TAB.Resources,
      node: (
        <Box {...styles('resourceColumns')}>
          {hasBlogs ? (
            <Box {...styles('resourceColumn')}>
              <SearchCategoryCards
                category={SITE_SEARCH_CATEGORY.Blogs}
                groups={groups}
                searchQuery={searchQuery}
              />
            </Box>
          ) : null}
          {hasCaseStudies ? (
            <Box
              {...styles(
                hasBlogs
                  ? ['resourceColumn', 'resourceColumnDivided']
                  : 'resourceColumn'
              )}
            >
              <SearchCategoryCards
                category={SITE_SEARCH_CATEGORY.CaseStudies}
                groups={groups}
                searchQuery={searchQuery}
              />
            </Box>
          ) : null}
        </Box>
      ),
    })
  }

  if (hasIndustries) {
    sections.push({
      key: SITE_SEARCH_CATEGORY.Industries,
      node: (
        <SearchCategoryCards
          category={SITE_SEARCH_CATEGORY.Industries}
          groups={groups}
          searchQuery={searchQuery}
        />
      ),
    })
  }

  if (hasHire) {
    sections.push({
      key: SITE_SEARCH_CATEGORY.Hire,
      node: (
        <SearchCategoryCards
          category={SITE_SEARCH_CATEGORY.Hire}
          groups={groups}
          searchQuery={searchQuery}
        />
      ),
    })
  }

  return (
    <Box {...styles('resultSections')}>
      {sections.map((section, index) => (
        <Box
          key={section.key}
          id={
            SEARCH_SECTION_IDS[section.key as keyof typeof SEARCH_SECTION_IDS]
          }
          {...styles(
            index > 0
              ? ['resultSectionBlock', 'resultSectionBlockDivided']
              : 'resultSectionBlock'
          )}
        >
          {section.node}
        </Box>
      ))}
    </Box>
  )
}

function SearchLoadingSkeleton() {
  const styles = getStyles(defaultStyles)

  return (
    <Box {...styles('resultsList')}>
      {Array.from({ length: 3 }).map((_, idx) => (
        <Box key={`search-skeleton-${idx}`} {...styles('skeletonCard')}>
          <Skeleton variant="rounded" height={196} />
          <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="95%" />
          <Skeleton variant="text" width="60%" />
        </Box>
      ))}
    </Box>
  )
}

function SearchQueryBody({
  isLoading,
  resultCount,
  groups,
  searchQuery,
  searchNoResultsMessage,
}: {
  isLoading: boolean
  resultCount: number
  groups?: SiteSearchGroupedItems
  searchQuery: string
  searchNoResultsMessage?: string
}) {
  if (isLoading) return <SearchLoadingSkeleton />
  if (resultCount === 0) {
    return (
      <SearchEmptyBanner
        message={searchNoResultsMessage?.trim() || DEFAULT_NO_RESULTS_MESSAGE}
      />
    )
  }
  if (!groups) return null
  return <SearchResultSections groups={groups} searchQuery={searchQuery} />
}

function SearchQueryResults({
  isLoading,
  resultCount,
  displayQuery,
  groups,
  searchNoResultsMessage,
}: {
  isLoading: boolean
  resultCount: number
  displayQuery: string
  groups?: SiteSearchGroupedItems
  searchNoResultsMessage?: string
}) {
  const styles = getStyles(defaultStyles)
  const showMeta = isLoading || resultCount > 0

  return (
    <Box id={SEARCH_SECTION_IDS[SITE_SEARCH_TAB.All]} {...styles('resultsTop')}>
      {showMeta ? (
        <Box {...styles('metaRow')}>
          <Typography variant="body2.500" color="text.secondary">
            {isLoading
              ? 'Searching…'
              : `Showing ${resultCount} result${resultCount === 1 ? '' : 's'} for “${displayQuery}”`}
          </Typography>
          {isLoading ? <CircularProgress size={20} /> : null}
        </Box>
      ) : null}

      <SearchQueryBody
        isLoading={isLoading}
        resultCount={resultCount}
        groups={groups}
        searchQuery={displayQuery}
        searchNoResultsMessage={searchNoResultsMessage}
      />
    </Box>
  )
}

function getSearchTabCount(
  category: SiteSearchTab,
  groups?: SiteSearchGroupedItems
) {
  if (!groups) return 0
  if (category === SITE_SEARCH_TAB.All) {
    return countGroupedResults(groups)
  }
  if (category === SITE_SEARCH_TAB.Resources) {
    return countResourceResults(groups)
  }
  if (category === SITE_SEARCH_TAB.Industries) {
    return groups[SITE_SEARCH_CATEGORY.Industries].length
  }
  if (category === SITE_SEARCH_TAB.Hire) {
    return groups[SITE_SEARCH_CATEGORY.Hire].length
  }
  return groups[SITE_SEARCH_CATEGORY.Services].length
}

function SearchCategoryTabs({
  tabsStickyTop,
  activeTabIndex,
  onCategoryChange,
  groups,
}: {
  tabsStickyTop: number
  activeTabIndex: number
  onCategoryChange: (_event: SyntheticEvent, nextIndex: number) => void
  groups?: SiteSearchGroupedItems
}) {
  const styles = getStyles(defaultStyles)

  return (
    <Box
      id={SEARCH_TABS_ELEMENT_ID}
      {...styles('tabsSection', {
        top: { md: `${tabsStickyTop}px` },
      })}
    >
      <Box {...styles('tabsInner')}>
        <Tabs
          {...styles('categoryTabs')}
          value={activeTabIndex}
          onChange={onCategoryChange}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile
        >
          {SITE_SEARCH_TABS.map((category, index) => (
            <Tab
              key={category}
              label={`${SITE_SEARCH_TAB_LABELS[category]} (${getSearchTabCount(category, groups)})`}
              value={index}
            />
          ))}
        </Tabs>
      </Box>
    </Box>
  )
}

function shouldShowDefaultSearchCards(
  hasCards: boolean,
  hasQuery: boolean,
  isLoading: boolean
) {
  if (!hasCards) return false
  if (!hasQuery) return true
  return !isLoading
}
export function SearchPage({
  defaultSearchCards,
  searchNoResultsMessage,
}: SearchPageProps) {
  const styles = getStyles(defaultStyles)
  const searchParams = useSearchParams()
  const qFromUrl = searchParams.get(SEARCH_QUERY_PARAM) ?? ''
  const categoryFromUrl = parseSiteSearchCategory(
    searchParams.get(SEARCH_CATEGORY_PARAM)
  )
  const tabsStickyTop = useStickySearchTabsTop()

  const {
    search,
    debouncedSearchValue,
    data: groups,
    isLoading,
  } = useSiteWideSearch({
    enabled: true,
    countPerType: SEARCH_CARDS_PER_CATEGORY,
    initialSearchValue: qFromUrl,
    searchDebounceTime: SEARCH_DEBOUNCE_MS,
  })

  useEffect(() => {
    search(qFromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL only
  }, [qFromUrl])

  const hasQuery = qFromUrl.trim().length > 0
  const resultCount = countGroupedResults(groups)
  const displayQuery = debouncedSearchValue.trim() || qFromUrl.trim()
  const showCategoryTabs = hasQuery && (isLoading || resultCount > 0)
  const showDefaultCards = shouldShowDefaultSearchCards(
    Boolean(defaultSearchCards),
    hasQuery,
    isLoading
  )
  const defaultCardsOffset = hasQuery && resultCount > 0

  const { activeCategory, handleCategoryChange } = useSearchScrollNavigation({
    hasQuery,
    isLoading,
    resultCount,
    groups,
    categoryFromUrl,
    qFromUrl,
  })
  const activeTabIndex = Math.max(0, SITE_SEARCH_TABS.indexOf(activeCategory))

  return (
    <>
      {showCategoryTabs ? (
        <SearchCategoryTabs
          tabsStickyTop={tabsStickyTop}
          activeTabIndex={activeTabIndex}
          onCategoryChange={handleCategoryChange}
          groups={groups}
        />
      ) : null}
      <Box {...styles('searchPage')}>
        <Box {...styles('wrapper')}>
          {hasQuery ? (
            <SearchQueryResults
              isLoading={isLoading}
              resultCount={resultCount}
              displayQuery={displayQuery}
              groups={groups}
              searchNoResultsMessage={searchNoResultsMessage}
            />
          ) : null}

          {showDefaultCards && defaultSearchCards ? (
            <Box
              {...(defaultCardsOffset
                ? styles('defaultCardsAfterResults')
                : {})}
            >
              <DefaultSearchCards data={defaultSearchCards} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </>
  )
}
